import type { PerspectiveBackboneModel } from 'aqTypes';
import { preventConcurrency } from 'utils/preventConcurrency';
import { dispatchAction } from '@ardoq/rxbeach';
import {
  setActivePerspective,
  triggerFiltersChangedEvent,
} from 'streams/filters/FilterActions';
import { replaceAllDynamicFilters } from 'filters/dynamicFilters';
import { globalFilters as Filters } from 'collections/filters';
import { groupingRuleOperations } from '@ardoq/core';
import {
  addGroupingRules,
  setGroupingRules,
} from 'streams/grouping/GroupingActions';
import { GroupByAttributes } from 'models/types';
import { getModifiersAsQueryString } from 'perspectives/util';
import { notifyPerspectiveCleared } from 'perspective/actions';
import { isDiscoverJourney } from 'canvas/isDiscoverJourney';
import { Features } from '@ardoq/api-types';
import { hasFeature } from 'modelInterface/features/featureInterface';
import { pushGroupingAndFormattingRulesToHistory } from 'canvas/contextToolBar/loadedStateHistory/loadedStateHistoryActions';
import { v4 as uuid } from 'uuid';

type ClearPerspectivesArgs = {
  shouldTriggerChangeEvent: boolean;
};
export const clearPerspectives = ({
  shouldTriggerChangeEvent,
}: ClearPerspectivesArgs) => {
  const transactionId = uuid();
  if (isDiscoverJourney() || hasFeature(Features.CANVAS_PROTOTYPE)) {
    dispatchAction(
      pushGroupingAndFormattingRulesToHistory({
        groupBys: {
          groupingRules: [],
          transactionId,
        },
        conditionalFormatting: {
          rules: [],
          transactionId,
        },
        transactionId,
      })
    );
  }
  replaceAllDynamicFilters([]);
  Filters.removeAllFilters({ shouldTriggerChangeEvent: false });
  Filters.transactionId = transactionId;
  dispatchAction(setGroupingRules({ groupingRules: [], transactionId }));
  dispatchAction(notifyPerspectiveCleared());
  dispatchAction(setActivePerspective({ id: null, queryString: '' }));
  if (shouldTriggerChangeEvent) {
    dispatchAction(triggerFiltersChangedEvent());
  }
};

export const resetPerspectives = () => {
  clearPerspectives({ shouldTriggerChangeEvent: true });
};

/** not safe for concurrent async calls. */
const loadPerspectiveUnsafe = async (
  perspective?: PerspectiveBackboneModel
): Promise<void> => {
  resetPerspectives();
  if (!perspective) {
    return;
  }
  await replaceAllDynamicFilters(perspective.get('dynamicFilters') || []);
  Filters.loadFilters({
    ...perspective.get('filters'),
    shouldTriggerChangeEvent: false,
  });

  const groupBys: GroupByAttributes[] | null = perspective.get('groupBys');
  if (groupBys) {
    dispatchAction(addGroupingRules({ groupingRules: groupBys }));
  }

  dispatchAction(
    setActivePerspective({
      id: perspective.id,
      queryString: getModifiersAsQueryString(
        groupingRuleOperations.getValidRules(groupBys ?? [])
      ),
    })
  );

  dispatchAction(triggerFiltersChangedEvent());
};
export const loadPerspective = preventConcurrency<
  void,
  typeof loadPerspectiveUnsafe
>(loadPerspectiveUnsafe);
